/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';

import { DeviceModel } from 'src/core/device';
import { CursorManager } from 'src/core/navigation/CursorManager';

import { InteractiveComponents } from '../../../../core/enums';
import { Locale, LocaleKeys } from '../../../../core/locale';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { setCurrentHighlightIndexProgramScreenAction } from '../../../../store/programscreen/programscreen.actions';
import {
    initBiaxialHorizontalStrip,
    resetBiaxialHorizontalStrip,
    updateViewAllDirectionProgramScreen,
} from '../../../../store/programscreen/programscreen.reducers';
import { ProgramScreenConstants } from '../../../../store/programscreen/ProgramScreenConstants';
import { Caret, Mousetrap } from '../../../common';
import { THorizontalActionStripLayout } from '../THorizontalActionStripItem';

import './BiaxialHorizontalList.scss';

interface HorizontalListProps<T> {
    items: T[];
    renderItem: (item: T, highlighted: boolean) => React.ReactNode;
    initialHighlightIndex: number;
    type: string;
    isDataAvailable: boolean;
    contentType: string;
    layoutType?: THorizontalActionStripLayout;
}

// TODO: need to find a way to remove this any
const BiaxialHorizontalList: FunctionComponent<HorizontalListProps<any>> = ({
    items,
    renderItem,
    type,
    isDataAvailable,
    contentType,
    layoutType,
}): JSX.Element => {
    const heightClassName = type.includes('image') ? 'image-height' : 'text-height';
    const [listItems, setListItems] = useState<React.ReactNode[]>([]);

    const dispatch = useTivoDispatch();

    const currentItemIndex = useTivoSelector(
        (state: RootState) => state.programScreen.currentHighlightIndex,
    );
    const activeComponent = useTivoSelector(
        (state: RootState) => state.interactiveLayer.activeComponent,
    );
    const isCaretVisible = useTivoSelector(
        (state: RootState) => state.programScreen.isCaretVisible,
    );
    const isGradientVisible = useTivoSelector(
        (state: RootState) => state.programScreen.isGradientVisible,
    );
    const [isCursorActive, setIsCursorActive] = useState<boolean>(CursorManager.isCursorAppeared());

    const showRightGradient = useTivoSelector(
        (state: RootState) => state.programScreen.showRightGradient,
    );

    const showLeftGradient = useTivoSelector(
        (state: RootState) => state.programScreen.showLeftGradient,
    );

    const onCursorStateChangeCallback = (state: boolean) => {
        setIsCursorActive(state);
    };

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        dispatch(initBiaxialHorizontalStrip(0, items.length));
        return () => {
            CursorManager.unsubscribe(refCursorManager);
            dispatch(resetBiaxialHorizontalStrip());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // TODO: need to confirm with UI specs for option stickiness behaviour
        dispatch(
            initBiaxialHorizontalStrip(
                items.length ? currentItemIndex % items.length : 0,
                items.length,
            ),
        );
        if (
            items[currentItemIndex]?.type === 'PosterImage' ||
            items[currentItemIndex]?.type === 'LandscapeImage'
        ) {
            if (items.length === 1) dispatch(setCurrentHighlightIndexProgramScreenAction(0));
            else
                dispatch(
                    setCurrentHighlightIndexProgramScreenAction(
                        contentType.includes('movie') && items.length >= 8 ? 2 : 1,
                    ),
                );
        }
        const renderItems = items.map((item, id) => renderItem(item, id === currentItemIndex));
        setListItems(renderItems);
        return () => {
            setListItems([]);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRightGradient, showLeftGradient, items]);

    useEffect(() => {
        const renderItems = items.map((item, id) => renderItem(item, id === currentItemIndex));
        if (type !== 'image' && items.length > 1) {
            const virtualElement = <div key="virtual-element" className="virtual-item" />;
            renderItems.push(virtualElement);
        }

        setListItems(renderItems);
        return () => {
            setListItems([]);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentItemIndex, items, type]);

    useEffect(() => {
        if (type === 'image') dispatch(updateViewAllDirectionProgramScreen());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);

    let leftIndex = -1;

    const getLeftListItems = () => {
        if (listItems.length > 1) {
            if (
                (contentType.includes('movie') && type === 'image') ||
                layoutType === ProgramScreenConstants.CAST
            ) {
                const renderItems = listItems;
                const leftItems: React.ReactNode[] = [];
                for (let i = 0; i < currentItemIndex; i += 1) {
                    leftItems.push(renderItems[i]);
                }
                return leftItems;
            }
            if (type !== 'image' && currentItemIndex === 0 && listItems.length > 4) {
                const leftItems: React.ReactNode[] = [];
                leftItems.push(listItems[listItems.length - 2]);
                leftItems.push(listItems[listItems.length - 1]);
                return leftItems;
            }

            leftIndex = (listItems.length + currentItemIndex - 1) % listItems.length;
            return listItems[leftIndex];
        }
        return null;
    };

    const getRightListItems = () => {
        if (listItems.length > 1) {
            const renderItems = listItems;
            const rightItems: React.ReactNode[] = [];
            if (type !== 'image' && currentItemIndex === 0 && listItems.length > 4) {
                for (let i = currentItemIndex + 1; i < renderItems.length - 2; i += 1) {
                    rightItems.push(renderItems[i]);
                }
                return rightItems;
            }
            for (let i = currentItemIndex + 1; i < renderItems.length; i += 1) {
                if (i !== leftIndex) {
                    rightItems.push(renderItems[i]);
                }
            }

            if (
                type === 'image' &&
                ![
                    ProgramScreenConstants.ALL_EPISODES,
                    ProgramScreenConstants.ON_DEMAND_EPISODES,
                    ProgramScreenConstants.CAST,
                ].includes(layoutType!)
            )
                return rightItems;

            for (let i = 0; i < currentItemIndex; i += 1) {
                if (i !== leftIndex) {
                    rightItems.push(renderItems[i]);
                }
            }
            return rightItems;
        }
        return null;
    };

    const getEmptyListItems = () => {
        return (
            <div className="empty-list-container">
                {Locale.getString(LocaleKeys.PS_EMPTY_TILE_DESCRIPTION)}
            </div>
        );
    };

    return (
        <div className={`horizontal-list-container ${heightClassName}`}>
            <Caret
                direction={InteractiveComponents.WEST}
                visible={
                    DeviceModel.isWebOsPlatform() &&
                    isCursorActive &&
                    isCaretVisible &&
                    activeComponent !== InteractiveComponents.NORTH &&
                    activeComponent !== InteractiveComponents.SOUTH
                }
                active={activeComponent === InteractiveComponents.WEST}
                className="program-west"
            />
            <Mousetrap
                key={`horizontal-list-item-left`}
                value={'list-container-west'}
                className={`horizontal-list-item left ${isGradientVisible ? 'active' : ''}`}
            >
                {isDataAvailable && getLeftListItems()}
            </Mousetrap>
            <Mousetrap
                key={`horizontal-list-item-highlight-item`}
                value={'list-container-centre'}
                className={'horizontal-list-item centre'}
            >
                {type !== ProgramScreenConstants.TEXT && showLeftGradient && (
                    <div className="gradient-overlay gradient-left" />
                )}
                {
                    // eslint-disable-next-line no-nested-ternary
                    !isDataAvailable
                        ? listItems[0]
                        : type === ProgramScreenConstants.TEXT &&
                            currentItemIndex === listItems.length - 1
                          ? listItems[0]
                          : listItems[currentItemIndex]
                }
                {type !== ProgramScreenConstants.TEXT && showRightGradient && (
                    <div className="gradient-overlay gradient-right" />
                )}
            </Mousetrap>
            <Mousetrap
                key={`horizontal-list-item-right`}
                value={'list-container-east'}
                className={`horizontal-list-item right ${isGradientVisible ? 'active' : ''}`}
            >
                {isDataAvailable ? getRightListItems() : getEmptyListItems()}
            </Mousetrap>
            <Caret
                direction={InteractiveComponents.EAST}
                visible={
                    DeviceModel.isWebOsPlatform() &&
                    isCursorActive &&
                    isCaretVisible &&
                    activeComponent !== InteractiveComponents.NORTH &&
                    activeComponent !== InteractiveComponents.SOUTH
                }
                active={activeComponent === InteractiveComponents.EAST}
                className="program-east"
            />
        </div>
    );
};

export default BiaxialHorizontalList;
